import { appContext } from '../../../../AppContext';
import { IbssComponent } from '../../../../Components/Core/BaseComponent/IbssComponent';
import { ChangeEvent, JSX } from 'react';
import { IOntologyNode, ITreeViewRulesData, ITreeViewRule } from './DataModels';
import { SerendieSymbol } from '@serendie/symbols';
import { Button, ButtonBar, Card, DataGrid, DeleteButton, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, OnSectionUpdateHandler, RightButtons, Select, Switch, TextArea, TextField } from "@iconics/components";
import Helper from '../../../../Common/Helper';
import { Guid } from '@iconics/core';
import { CheckedChangeDetails } from '@ark-ui/react/dist/components/switch/switch';
import { SelectionDetails } from '@zag-js/select';

class VirtualTreeViewRulesEditor extends IbssComponent<IProps, IState>
{
    private get labels() { return appContext().labels; }
    
    private ruleTypes = [
    //    { label: this.labels.TreeViewRule_RuleTypes_Filter, value: 'Filter'}, not supported yet
        { label: this.labels.TreeViewRule_RuleTypes_Real, value: 'Real'},
    ];

    // we can add a new rule only if no rule is assigned to the leaf node
    private get canAdd(): boolean
    {
        return this.getFirstAvailableLevel() !== -1;
    }

    constructor(props: IProps)
    {
        super(props);
        this.state =
        {
            selectedRule: undefined,
            rules: [],
            gridRefreshToken: Guid.new().toString(),
            gridData: { rows: [] },
            nodes: []
        };
    }

    public async componentDidUpdate(prevProps: IProps): Promise<void>
    {
        if (prevProps.rules !== this.props.rules
            || prevProps.ontologyNodes !== this.props.ontologyNodes)
        {
            const nodes = this.props.ontologyNodes?.length > 0
                ? this.buildOntologyNodesList(this.props.ontologyNodes)
                : [];

            const rules = this.buildValidRules(this.props.rules.TreeViewRules, nodes);

            this.setState({ 
                nodes,
                rules,
                gridRefreshToken: Guid.new().toString(),
                selectedRule: undefined
        });
        }
    }

    private buildValidRules(sourceRules: ITreeViewRule[], nodes: IOrderedOntologyNode[]): ITreeViewRule[]
    {
        const nodeLevelById = new Map<string, number>(nodes.map(n => [n.Id, n.Level]));
        const seenLevels = new Set<number>();

        const validRules: ITreeViewRule[] = [];
        sourceRules
            .slice()
            .forEach(rule => {
                const level = nodeLevelById.get(rule.OntologyNodeId);
                if (level === undefined || seenLevels.has(level))
                    return;

                seenLevels.add(level);
                validRules.push({
                    ...rule,
                    Id: Guid.new().toString(),
                    Order: validRules.length
                });
            });

        return validRules;
    }

    // build an ordered list from an arbitrary forest (multiple roots, any number of children)
    private buildOntologyNodesList(nodes: IOntologyNode[]): IOrderedOntologyNode[]
    {
        const orderedList: IOrderedOntologyNode[] = [];
        const nodeById = new Map<string, IOntologyNode>(nodes.map(node => [node.Id, node]));
        const parentToChildren = new Map<string | null, IOntologyNode[]>();

        const addChild = (parentId: string | null, child: IOntologyNode) => {
            const list = parentToChildren.get(parentId) ?? [];
            list.push(child);
            parentToChildren.set(parentId, list);
        };

        nodes.forEach(node => {
            const parentId = node.Parent_ID && nodeById.has(node.Parent_ID)
                ? node.Parent_ID
                : null;
            addChild(parentId, node);
        });

        const sortByName = (a: IOntologyNode, b: IOntologyNode) => a.Name.localeCompare(b.Name);
        const visited = new Set<string>();

        const visit = (node: IOntologyNode, level: number) => {
            if (visited.has(node.Id))
                return;

            visited.add(node.Id);
            orderedList.push({
                Id: node.Id,
                Name: node.Name,
                Level: level
            });

            const children = (parentToChildren.get(node.Id) ?? []).slice().sort(sortByName);
            children.forEach(child => visit(child, level + 1));
        };

        const roots = (parentToChildren.get(null) ?? []).slice().sort(sortByName);
        roots.forEach(root => visit(root, 0));

        nodes.forEach(node => {
            if (!visited.has(node.Id))
                visit(node, 0);
        });

        return orderedList;
    }

    private getAssignedLevels(excludeRuleId?: string): Set<number>
    {
        const nodeLevelById = new Map<string, number>(this.state.nodes.map(n => [n.Id, n.Level]));
        const assignedLevels = new Set<number>();

        this.state.rules.forEach(rule => {
            if (excludeRuleId && rule.Id === excludeRuleId)
                return;

            const level = nodeLevelById.get(rule.OntologyNodeId);
            if (level !== undefined)
                assignedLevels.add(level);
        });

        return assignedLevels;
    }

    private getAvailableNodes(): {value: string, label: string}[]
    {
        const { selectedRule } = this.state;
        if (!selectedRule)
            return [];

        const selectedNodeId = selectedRule.OntologyNodeId;
        const levelBounds = this.getAllowedLevelBoundsForRule(selectedRule);
        const assignedLevels = this.getAssignedLevels(selectedRule.Id);

        return this.state.nodes
            .filter(node => {
                if (node.Id === selectedNodeId)
                    return true;

                if (node.Level < levelBounds.minLevel || node.Level > levelBounds.maxLevel)
                    return false;

                return !assignedLevels.has(node.Level);
            })
            .map(node => ({ value: node.Id, label: node.Name }));
    }

    private getAllowedLevelBoundsForRule(selectedRule: ITreeViewRule): { minLevel: number, maxLevel: number }
    {
        const orderedRules = this.state.rules.slice().sort((a, b) => a.Order - b.Order);
        const selectedIndex = orderedRules.findIndex(r => r.Id === selectedRule.Id);
        const distinctLevels: number[] = Array.from(new Set<number>(this.state.nodes.map(n => n.Level))).sort((a, b) => a - b);

        if (selectedIndex === -1 || distinctLevels.length === 0)
            return { minLevel: 0, maxLevel: Number.MAX_SAFE_INTEGER };

        const nodeLevelById = new Map<string, number>(this.state.nodes.map(n => [n.Id, n.Level]));
        let minLevel = distinctLevels[0];
        let maxLevel = distinctLevels[distinctLevels.length - 1];

        for (let i = selectedIndex - 1; i >= 0; i--)
        {
            const level = nodeLevelById.get(orderedRules[i].OntologyNodeId);
            if (level !== undefined)
            {
                minLevel = level + 1;
                break;
            }
        }

        for (let i = selectedIndex + 1; i < orderedRules.length; i++)
        {
            const level = nodeLevelById.get(orderedRules[i].OntologyNodeId);
            if (level !== undefined)
            {
                maxLevel = level - 1;
                break;
            }
        }

        return { minLevel, maxLevel };
    }

    private getAvailableFilterTypes(): {value: string, label: string}[]
    {
        const filterTypes: {value: string, label: string}[] = [];

        switch (this.state.selectedRule?.RuleType)
        {
            case 'Filter':
                filterTypes.push({ label: this.labels.TreeViewRule_FilterTypes_Expression, value: 'Expression'});
                break;
            default:
                filterTypes.push({ label: this.labels.TreeViewRule_FilterTypes_None, value: 'None'});
                break;
                
        }
        return filterTypes;
    }

    private getFirstAvailableLevel(): number
    {
        const distinctLevels: number[] = Array.from(new Set<number>(this.state.nodes.map(n => n.Level))).sort((a, b) => a - b);
        if (distinctLevels.length === 0)
            return -1;

        const assignedLevels = this.getAssignedLevels();
        const firstAvailableLevel = distinctLevels.find(level => !assignedLevels.has(level));
        return firstAvailableLevel ?? -1;
    }

    private handleDelete(): Promise<void>
    {
        const {selectedRule} = this.state;
        if (!selectedRule)
            return Promise.resolve();
        
        let i = 0;
        const rules = this.state.rules.filter(r => r.Id !== selectedRule.Id).map(r => {
            r.Order = i++;
            return r;
        });

        this.setState({
            selectedRule: undefined,
            gridRefreshToken: Guid.new().toString(),
            rules: rules
        });

        this.props.onDefinitionUpdate(rules);
        return Promise.resolve();
    }

    private handleAdd(): void
    {
        const availableLevel = this.getFirstAvailableLevel();
        if (availableLevel === -1)
            return;

        const nodeForLevel = this.state.nodes.find(n => n.Level === availableLevel);
        if (!nodeForLevel)
            return;

        const newRuleIndex = this.state.rules.length;
        const rule = {
            RuleType: this.ruleTypes[0].value,
            FilterType: 'None',
            FilterValue: '',
            KeepEmptyAssets: false,
            UseFullNames: false,
            Order: newRuleIndex,
            Name: '',
            OntologyNodeId: nodeForLevel.Id,
            Nested: false,
            Id: Guid.new().toString()
        };

        const { rules } = this.state;
        rules.push(rule);

        this.setState({
            rules: rules,
            selectedRule: rule,
            gridRefreshToken: Guid.new().toString(),
        });
        this.props.onDefinitionUpdate(rules);
    }
    
    private handleCellClicked(i: ITreeViewRule): void 
    {
        const { gridData } = this.state;
        gridData.checkedIds = [ i.Id ];
        this.setState({ gridData, selectedRule: i })
    }

    private handleRuleTypeChange(e: SelectionDetails): void 
    {
        if (!this.state.selectedRule)
            return;
        this.state.selectedRule.RuleType = e.value;
        return this.setState({
            selectedRule: this.state.selectedRule
        });
    }

    private handleRuleAtLevelChange(e: SelectionDetails): void 
    {
        const { selectedRule } = this.state;
        if (!selectedRule)
            return;

        const node = this.state.nodes.find(n => n.Id === e.value);

        if (!node)
            throw new Error("No available nodes for new rule assignment.");

        const levelBounds = this.getAllowedLevelBoundsForRule(selectedRule);
        if (node.Level < levelBounds.minLevel || node.Level > levelBounds.maxLevel)
            return;

        const assignedLevels = this.getAssignedLevels(selectedRule.Id);
        if (assignedLevels.has(node.Level))
            return;

        selectedRule.OntologyNodeId = e.value;
        this.setState({ selectedRule });
    }

    private handleFilterValueChange(e: ChangeEvent<HTMLTextAreaElement, HTMLTextAreaElement>): void 
    {
        if (!this.state.selectedRule)
            return;
        this.state.selectedRule.FilterValue = e.target.value;
        return this.setState({
            selectedRule: this.state.selectedRule
        });
                                    
    }
    private handleFilterTypeChange(e: SelectionDetails): void 
    {
        if (!this.state.selectedRule)
            return;
        this.state.selectedRule.FilterType = e.value;
        return this.setState({
            selectedRule: this.state.selectedRule
        });
    }

    private handleKeepEmptyAssetsChange(e: CheckedChangeDetails): void 
    {
        if (!this.state.selectedRule)
            return;
        this.state.selectedRule.KeepEmptyAssets = e.checked;
        return this.setState({
            selectedRule: this.state.selectedRule
        });
    }

    private handleUseFullNameChange(e: CheckedChangeDetails): void 
    {
        if (!this.state.selectedRule)
            return;
        this.state.selectedRule.UseFullNames = e.checked;
        return this.setState({
            selectedRule: this.state.selectedRule
        });
    }
    
    private handleGridChange(i: IGridData<ITreeViewRule>): void 
    {
        const ids = i.checkedIds;
        if (ids && ids.length > 0)
            this.setState({ gridData: i, selectedRule: this.state.rules.find(r => r.Id === ids[0]) });
        else
            this.setState({ gridData: i, selectedRule: undefined });
    }

    private async handleRowsRequest(request: IGridRowsRequest<ITreeViewRule>): Promise<IGridRowsResponse<ITreeViewRule>>
    {
        return {
            rows: this.state.rules,
            nextSkipToken: ''
        };
    }

    public render(): JSX.Element
    {
        return (
            
            <Card
                title={this.labels.Platform_VirtualTreeView_TreeViewRulesTitle}
                subTitle={this.labels.Platform_VirtualTreeView_TreeViewRulesSubTitle}
                onSectionUpdate={i => this.props.onSectionUpdate(i)}
            >
                <GridContainer>
                    <GridItem expanded={6}>
                        <GridContainer>
                            <GridItem>
                                <ButtonBar>
                                    <RightButtons>
                                        <DeleteButton
                                            small={true}
                                            onDelete={() => this.handleDelete()}
                                            disabled={!this.state.selectedRule}
                                        />
                                        <Button
                                            size='small'
                                            styleType='ghost'
                                            leftIcon={<SerendieSymbol name='plus' />}
                                            onClick={() => this.handleAdd()}
                                            disabled={!this.canAdd}
                                        >
                                            {this.labels.Platform_CommonAction_Add}
                                        </Button>
                                    </RightButtons>
                                </ButtonBar>
                            </GridItem>
                            <GridItem>
                                
                                <DataGrid
                                    columns={[
                                        {
                                            columnId: Helper.nameOf<ITreeViewRule>("RuleType"),
                                            title: this.labels.TreeViewRule_RuleType,
                                            getCellValue: rule => rule.RuleType,
                                            columnWidth: 50,
                                        },
                                        {
                                            columnId: Helper.nameOf<ITreeViewRule>("OntologyNodeId"),
                                            title: this.labels.TreeViewRule_RuleAtLevel,
                                            getCellValue: rule => this.state.nodes.find(n => n.Id === rule.OntologyNodeId)?.Name ?? '',
                                            columnWidth: 50,
                                        }
                                    ]}
                                    data={this.state.gridData}
                                    rowId={i => i.Id}
                                    onChange={i => this.handleGridChange(i)}
                                    onRowsRequest={i => this.handleRowsRequest(i)}
                                    refreshToken={this.state.gridRefreshToken}
                                    onCellClicked={i => this.handleCellClicked(i)}
                                    showCheckBoxes={true}
                                    allowOnlyOneCheckBox={true}

                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    {this.state.selectedRule !== undefined && 
                    <GridItem expanded={6}>
                        <GridContainer spacing='xs'>
                            <GridItem>
                                <Select
                                    label={this.labels.TreeViewRule_RuleType}
                                    placeholder={this.labels.TreeViewRule_RuleType}
                                    items={this.ruleTypes.map(x => ({ value: x.value, label: x.label }))}
                                    onSelect={e => this.handleRuleTypeChange(e)}
                                    value={this.state.selectedRule?.RuleType ? [this.state.selectedRule.RuleType] : []}
                                />
                            </GridItem>
                            <GridItem>
                                <Select
                                    label={this.labels.TreeViewRule_RuleAtLevel}
                                    placeholder={this.labels.TreeViewRule_RuleAtLevel}
                                    items={this.getAvailableNodes()}
                                    onSelect={e => this.handleRuleAtLevelChange(e)}
                                    value={this.state.selectedRule?.OntologyNodeId ? [this.state.selectedRule.OntologyNodeId] : []}
                                />
                            </GridItem>
                            <GridItem>
                                { this.state.selectedRule?.RuleType === 'Real' ?
                                    <Select
                                        label={this.labels.TreeViewRule_FilterType}
                                        disabled
                                    />
                                :
                                    <Select
                                        label={this.labels.TreeViewRule_FilterType}
                                        placeholder={this.labels.TreeViewRule_FilterType}
                                        items={this.getAvailableFilterTypes()}
                                        onSelect={e => this.handleFilterTypeChange(e)}
                                        value={this.state.selectedRule?.FilterType ? [this.state.selectedRule.FilterType] : []}
                                        disabled={this.state.selectedRule?.RuleType === 'Real'}
                                    />
                                }
                            </GridItem>
                            <GridItem>
                                <Switch
                                    label={this.labels.TreeViewRule_UseFullNames}
                                    checked={this.state.selectedRule?.UseFullNames}
                                    onCheckedChange={e => this.handleUseFullNameChange(e)}
                                />
                            </GridItem>
                            <GridItem>
                                { this.state.selectedRule?.RuleType === 'Real' ?
                                    <TextField
                                        label={this.labels.TreeViewRule_FilterValue}
                                        disabled
                                    />
                                :
                                    <TextArea
                                        label={this.labels.TreeViewRule_FilterValue}
                                        placeholder={this.labels.TreeViewRule_FilterValue}
                                        value={this.state.selectedRule?.FilterValue ?? ''}
                                        onChange={e => this.handleFilterValueChange(e)}
                                        rows={15}
                                    />
                                }
                            </GridItem>
                            <GridItem>
                                <Switch
                                    label={this.labels.TreeViewRule_KeepEmptyAssets}
                                    checked={this.state.selectedRule?.KeepEmptyAssets}
                                    onCheckedChange={e => this.handleKeepEmptyAssetsChange(e) }
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    }
                </GridContainer>
            </Card>
        )
    }    
}

export default VirtualTreeViewRulesEditor;

export interface IProps
{
    ontologyNodes: IOntologyNode[];
    rules: ITreeViewRulesData;
    onSectionUpdate: OnSectionUpdateHandler;
    onDefinitionUpdate(rules: ITreeViewRule[]): void;
}

export interface IState 
{
    rules: ITreeViewRule[];
    selectedRule?: ITreeViewRule;
    nodes: IOrderedOntologyNode[];
    gridRefreshToken: string;
    gridData: IGridData<ITreeViewRule>;
    
}

export interface IOrderedOntologyNode
{
    Name: string;
    Id: string;
    Level: number;
}